import { defineStore } from "pinia";
import { type Ref, ref, computed } from "vue";
import Uchi from "@/models/Uchi/uchi";
import axios from "axios";
import apiUchi from "@/helpers/api/apiUchi";
import FileHelper from "@/helpers/utilities/FileHelper";

interface UchiSetting {
  key: string;
  value: string;
}

interface UchiFile {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface UchiUser {
  email?: string;
  [key: string]: unknown;
}

interface UchiPayload {
  externalShareId?: string;
  title?: string;
  expiresWhen?: string | Date;
  accessCodeRequired?: boolean;
  [key: string]: unknown;
}

interface UploadFileObject {
  file: File | Blob;
  name: string;
}

export const useUchiStore = defineStore("uchiD", () => {
  // 1) State refs
  const loading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);
  const uchiState: Ref<Uchi[]> = ref([]);
  const sortKey: Ref<string> = ref("sharedWithName");
  const setup: Ref<Record<string, string | boolean>> = ref({ colabEnabled_setup: true });
  const files: Ref<UchiFile[]> = ref([]);
  const users: Ref<UchiUser[]> = ref([]);
  const driveId: Ref<string> = ref("");
  const itemId: Ref<string> = ref("");
  const emailOptions: Ref<UchiUser[]> = ref([]);

  // 2) Computed getters (no args)
  const uchis = computed(() => uchiState.value);

  // 3) Function-getters (with args)
  const uchi = computed(
    () => (externalShareId: string) => uchiState.value.find(u => u.externalShareId == externalShareId)
  );
  const getDriveItemId = computed(
    () => (driveId: string, itemId: string) => uchiState.value.find(u => u.driveId == driveId && u.itemId == itemId)
  );
  const getDeleteFromFolderName = computed(
    () => (driveId: string, itemId: string) => uchiState.value.find(u => u.driveId == driveId && u.itemId == itemId)
  );

  // 3) Actions
  const reloadUchi = async () => {
    const [res1, res2] = await Promise.all([apiUchi.get(`/me/shares`), apiUchi.get(`/me/sharedwith/institution`)]);

    const uchis = res1.data.results.map((u: unknown) => {
      const item = new Uchi(u);

      item.owner = { displayName: null };
      return item;
    });

    const uchis2 = res2.data.results.map((u: unknown) => new Uchi(u));
    const combined = [...uchis, ...uchis2];
    uchiState.value = combined;
  };

  const loadUchi = async () => {
    loading.value = true;
    error.value = null;

    try {
      if (uchiState.value.length === 0) {
        return reloadUchi();
      }
    } catch (err: unknown) {
      error.value = (err as Error)?.message;
    } finally {
      loading.value = false;
    }
  };

  const loadSettings = async () => {
    const response = await apiUchi.get(`/me/setup/quickshare`);
    response.data.quickShare.forEach((element: UchiSetting) => {
      if (element.key === "code_subject") {
        setup.value["code_setup"] = element.value;
      }
      if (element.key === "default_accesscoderequired") {
        setup.value["defaultAccessCode_setup"] = element.value === "true";
      }
      if (element.key === "default_expiredays") {
        setup.value["defaultExpiredays_setup"] = element.value;
      }
      if (element.key === "default_usercanoverride") {
        setup.value["defaultUserCanOverride_setup"] = element.value === "true";
      }
      if (element.key === "colab_enabled") {
        setup.value["colabEnabled_setup"] = element.value === "true";
      }
    });
  };

  const searchEmails = async (searchString: string) => {
    if (!searchString) {
      emailOptions.value = [];
      return;
    }
    try {
      const response = await apiUchi.get(`/users/${searchString}`);

      emailOptions.value = response.data.filter(
        (e: UchiUser) =>
          e.email && typeof e.email === "string" && e.email.toLowerCase().includes(searchString.toLowerCase())
      );
    } catch {
      /* empty */
    }
  };

  const resetSearchEmails = () => {
    emailOptions.value = [];
  };

  const addUchi = async (uchi: UchiPayload) => {
    return await apiUchi.post("/shares", uchi);
  };

  const removeSubject = async (uchiId: string) => {
    await apiUchi.delete(`/shares/${uchiId}`);
    await reloadUchi();
    //uchiState.value = uchiState.value.filter(uId => uId.shareId !== id);
  };

  const removeAShareFile = async (uchiId: string, fileId: string) => {
    await apiUchi.delete(`/shares/${uchiId}/files/${fileId}`);
    files.value = files.value.filter(file => file.id !== fileId);
    await reloadUchi();
  };

  const updateUchiShare = async (uchi: UchiPayload) => {
    await apiUchi.patch(`/shares/${uchi.externalShareId}`, {
      title: uchi.title,
      expiresWhen: uchi.expiresWhen,
      accessCodeRequired: uchi.accessCodeRequired
    });

    await reloadUchi();
  };

  const uploadDriveItemToBlobStorage = async (shareId: string, fileObject: UploadFileObject) => {
    const binary = await FileHelper.getFileAsBinary(fileObject.file);
    const addFileResult = await apiUchi.post(`/shares/${shareId}/files`, { filename: fileObject.name });
    // Content-Length is set automatically by the browser from the request body —
    // setting it explicitly is a no-op and triggers a "Refused to set unsafe header" console warning.
    const uploadFileResult = await axios.put(addFileResult.data.uploadUrl, binary, {
      headers: {
        "Content-Type": "application/octet-stream",
        "x-ms-blob-type": "BlockBlob"
      }
    });
    return uploadFileResult.data;
  };

  const addUserToUchi = async (identifier: string, userData: UchiUser) => {
    const response = await apiUchi.post(`/shares/${identifier}/users`, userData);
    await reloadUchi();
    return response.data;
  };

  const removeUserFromUchi = async (identifier: string, userIdentifier: string) => {
    await apiUchi.delete(`/shares/${identifier}/users/${userIdentifier}`);

    await reloadUchi();
  };

  return {
    loading,
    error,
    uchiState,
    sortKey,
    setup,
    files,
    users,
    driveId,
    itemId,
    emailOptions,
    uchis,
    uchi,
    getDriveItemId,
    getDeleteFromFolderName,
    loadUchi,
    reloadUchi,
    loadSettings,
    searchEmails,
    resetSearchEmails,
    addUchi,
    removeSubject,
    removeAShareFile,
    updateUchiShare,
    uploadDriveItemToBlobStorage,
    addUserToUchi,
    removeUserFromUchi
  };
});
